import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  errorCodeSelector,
  warehouseSelector,
  warehousesSelector,
} from './store/selector';
import { matersApiService } from './services/api';
import {
  getWarehousesInitApiAction,
  getWarehousesSuccessApiAction,
  getWarehousesFailureApiAction,
  postWarehouseInitApiAction,
  postWarehouseSuccessApiAction,
  postWarehouseFailuireApiAction,
  putWarehouseInitApiAction,
  putWarehouseSuccessApiAction,
  putWarehouseFailuireApiAction,
  deleteWarehouseFailuireApiAction,
  deleteWarehouseSuccessApiAction,
  deleteWarehouseInitApiAction,
  setWarehouseAction,
} from './store/reducer';
import { PostWarehousePayloadRequest } from './interfaces/post-warehouse';
import { PutWarehousePayloadRequest } from './interfaces/put-warehouse';
import { Warehouse } from '../../interfaces/warehouse.interface';

export function useWarehouseCore() {
  const warehouses = useSelector(warehousesSelector);
  const errorCode = useSelector(errorCodeSelector);
  const warehouse = useSelector(warehouseSelector);
  const dispatch = useDispatch();

  const getWarehouses = useCallback(
    async (
      id: number,
      filters?: {
        code?: string;
        description?: string;
        channel_type_id?: string;
      },
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(getWarehousesInitApiAction());
        const response = await matersApiService.getWarehouses(id, filters);
        dispatch(getWarehousesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          getWarehousesFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const postWarehouse = useCallback(
    async (
      id: number,
      data: PostWarehousePayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postWarehouseInitApiAction());
        await matersApiService.postWarehouse(id, data);
        dispatch(postWarehouseSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postWarehouseFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const putWarehouse = useCallback(
    async (
      cId: number,
      tId: number,
      body: PutWarehousePayloadRequest,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(putWarehouseInitApiAction());
        await matersApiService.putWarehouse(cId, tId, body);
        dispatch(putWarehouseSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          putWarehouseFailuireApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const deleteWarehouse = useCallback(
    async (
      cId: number,
      tId: number,
      onSuccess?: () => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(deleteWarehouseInitApiAction());
        await matersApiService.deleteWarehouse(cId, tId);
        dispatch(deleteWarehouseSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(deleteWarehouseFailuireApiAction());
      }
    },
    [dispatch]
  );

  const setWarehouse = useCallback((store: Warehouse | null) => {
    dispatch(setWarehouseAction(store));
  }, [dispatch]);

  return {
    warehouses,
    warehouse,
    errorCode,
    getWarehouses,
    postWarehouse,
    putWarehouse,
    deleteWarehouse,
    setWarehouse,
  };
}
